
import { defineComponent, PropType } from 'vue';
import { Qalendar } from 'qalendar';
import dayjs from 'dayjs';

const CALENDAR_LOCALES: Record<string, string> = { pt: 'pt-BR', en: 'en-US' };

export default defineComponent({
  name: 'DsCalendar',

  components: { Qalendar },

  props: {
    events: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    mode: {
      type: String,
      default: 'month',
    },
    tooltip: {
      type: Function as PropType<(event: any) => string>,
      default: null,
    },
    config: {
      type: Object as PropType<Record<string, any>>,
      default: () => ({}),
    },
  },

  emits: ['event-click', 'edit-event', 'delete-event', 'updated-period', 'updated-mode'],

  computed: {
    defaultConfig(): Record<string, any> {
      return {
        locale: CALENDAR_LOCALES[this.$i18n.locale] || this.$i18n.locale,
        defaultMode: this.mode,
        isSilent: true,
        showCurrentTime: true,
        week: {
          startsOn: 'sunday',
        },
        dayBoundaries: {
          start: 0,
          end: 24,
        },
        dayIntervals: {
          length: 60,
          height: 30,
        },
        eventDialog: {
          isCustom: !!this.$slots.eventDialog,
        },
        style: {
          FontFamily: 'Inter',
          colorSchemes: {
            primary: {
              color: '#153c59',
              backgroundColor: '#DFEDFC',
              borderLeft: '4px solid #153c59',
            },
            period: {
              color: '#3F6480',
              backgroundColor: '#EEF4FA',
              borderLeft: '4px solid #7FA8C9',
            },
          },
        },
      };
    },

    calendarConfig(): Record<string, any> {
      const config: Record<string, any> = { ...this.defaultConfig };

      Object.entries(this.config).forEach(([key, value]) => {
        const current = config[key];

        config[key] =
          value && current && typeof value === 'object' && !Array.isArray(value)
            ? { ...current, ...value }
            : value;
      });

      return config;
    },

    calendarEvents(): any[] {
      return this.events.map((event: any) => ({
        colorScheme: 'primary',
        isCustom: true,
        ...event,
      }));
    },
  },

  methods: {
    eventHour(event: any){
      return event.time && event.time.start ? event.time.start.slice(11, 16) : '';
    },

    isRange(event: any){
      return !!(event.time && event.time.start) && !event.time.start.includes(' ');
    },

    rangePosition(event: any){
      if(event.rangePosition) return event.rangePosition;

      return this.isRange(event) ? 'single' : '';
    },

    rangeClasses(event: any){
      const position = this.rangePosition(event);
      if(!position || position === 'single') return [];

      return ['is-range', `is-range-${position}`];
    },

    showsRangeLabel(event: any){
      const position = this.rangePosition(event);

      return position === 'start' || (position === 'single' && this.isRange(event));
    },

    eventRange(event: any){
      const start = dayjs(event.time.start);
      const end = dayjs(event.time.end);

      return start.isSame(end, 'day')
        ? start.format('DD/MM')
        : `${start.format('DD/MM')} - ${end.format('DD/MM')}`;
    },

    eventTooltip(event: any){
      const lines = [
        this.tooltip ? this.tooltip(event) : '',
        this.$slots.eventDialog ? `↳ ${this.$t('calendar.clickHint')}` : '',
      ];

      return {
        value: lines.filter((line) => line).join('\n'),
        class: 'calendar-tooltip',
      };
    },
  },
});
